/* dc-landing.js — theming por color + interacciones de la landing (vanilla, defer) */
(() => {
  'use strict';

  const root = document.documentElement;

  const parseJSON = (el, attr) => {
    if (!el) return [];
    try { return JSON.parse(el.getAttribute(attr) || '[]'); } catch (e) { return []; }
  };

  class DCLanding extends HTMLElement {
    connectedCallback() {
      this.colors = parseJSON(this.querySelector('[data-dc-colors]'), 'data-dc-colors');
      this.variants = parseJSON(this.querySelector('[data-dc-variants]'), 'data-dc-variants');
      this.active = parseInt(this.dataset.active || '0', 10);

      // Regalo: el hero publica solo el id de la variante; todo lo demás ya viene
      // renderizado en el HTML. Si no hay nodo, no hay regalo activo.
      const giftNode = this.querySelector('[data-dc-gift]');
      this.gift = null;
      if (giftNode) {
        try { this.gift = JSON.parse(giftNode.getAttribute('data-dc-gift') || 'null'); } catch (e) { this.gift = null; }
      }
      this.injectGiftLines();
      this.syncGiftCompare();

      // Compra: se engancha SIEMPRE, haya o no variantes de color.
      // Todos los botones [data-dc-add] de la página: agregar al carrito → ir directo al checkout.
      if (!window.__dcBuyBound) {
        window.__dcBuyBound = true;
        document.addEventListener('submit', (e) => {
          const form = e.target;
          if (!form || typeof form.matches !== 'function' || !form.matches('[data-dc-add]')) return;
          e.preventDefault();
          this.addAndCheckout(form);
        });
      }

      // Clicks: swatches (solo si hay colores) y scroll al bloque de compra (siempre).
      this.addEventListener('click', (e) => {
        const gift = e.target.closest('[data-gift-cta]');
        if (gift) { e.preventDefault(); this.scrollToHeroCTA(); return; }
        const buy = e.target.closest('[data-scroll-buy]');
        if (buy) { e.preventDefault(); this.scrollToBuy(); return; }
        const sw = e.target.closest('[data-color-index]');
        if (sw && this.colors.length) { e.preventDefault(); this.select(parseInt(sw.dataset.colorIndex, 10), true); }
      });

      // Parallax sutil del hero.
      // Se recalcula dentro de un requestAnimationFrame (un cálculo por frame como
      // mucho) y solo mientras el hero sigue en pantalla. Antes se leía la posición
      // y se reescribía el estilo en CADA evento de scroll, lo que forzaba un
      // recálculo de layout síncrono y dejaba el hilo principal ocupado.
      const parallax = this.querySelector('[data-parallax]');
      const reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.reduceMotion = reduceMotion;
      // Solo en escritorio. En movil esto reescribe object-position de la foto del
      // hero —que es el elemento LCP— en cada frame de scroll, y repintar una foto
      // a ancho completo dentro del navegador integrado de Instagram es de lo que
      // mas ocupa el hilo principal (INP). En 390px el efecto ademas no se aprecia.
      const wideScreen = !window.matchMedia || window.matchMedia('(min-width: 860px)').matches;
      if (parallax && !reduceMotion && wideScreen) {
        let ticking = false;
        const applyParallax = () => {
          ticking = false;
          const r = parallax.getBoundingClientRect();
          if (r.bottom <= 0 || r.top >= window.innerHeight) return; // fuera de pantalla: no tocamos nada
          parallax.style.objectPosition = '50% ' + (50 + Math.max(-8, Math.min(8, r.top / 60))) + '%';
        };
        this._onScroll = () => {
          if (ticking) return;
          ticking = true;
          requestAnimationFrame(applyParallax);
        };
        window.addEventListener('scroll', this._onScroll, { passive: true });
      }

      // Va ANTES del return de abajo a propósito: un producto sin variantes de color
      // igual puede tener regalo, y si esto quedara después nunca se revelaría.
      this.revealGiftPieces();
      // Mismo motivo: el timbre del CTA no depende de que haya colores.
      this.startCtaRing();

      // Theming por color: solo aplica si el producto tiene colores.
      if (!this.colors.length) return;
      this.select(this.active, false);
    }

    disconnectedCallback() {
      if (this._onScroll) window.removeEventListener('scroll', this._onScroll);
      if (this._ringTimer) clearInterval(this._ringTimer);
      if (this._ringObserver) this._ringObserver.disconnect();
    }

    // Timbre del CTA: un meneo corto cada N segundos, encima de la respiración.
    // El movimiento entero vive en el CSS (.dc-cta-ring.is-ringing); aquí solo se
    // decide CUÁNDO. Tres frenos para no gastar batería ni marear:
    //   · fuera de pantalla no suena —menear algo que nadie ve es solo repintar—,
    //   · con la pestaña en segundo plano tampoco,
    //   · y si el botón está deshabilitado (producto agotado) no hay nada que
    //     empujar, así que se queda quieto.
    startCtaRing() {
      const wrap = this.querySelector('[data-cta-ring]');
      if (!wrap || this.reduceMotion) return;
      const every = parseFloat(wrap.dataset.ctaRing);
      if (!every || every <= 0) return;

      const btn = wrap.querySelector('[type="submit"]');
      let onScreen = true;

      // La clase se quita al acabar la animación y no con un temporizador aparte:
      // así el reloj del CSS y el del JS no pueden desincronizarse.
      wrap.addEventListener('animationend', () => wrap.classList.remove('is-ringing'));

      if ('IntersectionObserver' in window) {
        this._ringObserver = new IntersectionObserver(
          (entries) => { onScreen = entries[0].isIntersecting; },
          { threshold: 0.5 }
        );
        this._ringObserver.observe(wrap);
      }

      this._ringTimer = setInterval(() => {
        if (!onScreen || document.hidden) return;
        if (btn && btn.disabled) return;
        // Quitar, forzar reflow y volver a poner: si el meneo anterior aún no
        // hubiera terminado, sin esto el navegador ignoraría la clase repetida.
        wrap.classList.remove('is-ringing');
        void wrap.offsetWidth;
        wrap.classList.add('is-ringing');
      }, every * 1000);
    }

    // El CTA final y la barra flotante son secciones distintas y no ven los ajustes
    // del hero, así que no pueden renderizar la línea del regalo por su cuenta.
    // En vez de duplicar la configuración en tres sitios, el hero la publica una vez
    // y aquí se la inyectamos a todos los formularios de compra de la página.
    // El formulario del hero ya la trae escrita en el HTML y se salta solo.
    injectGiftLines() {
      const gid = this.gift && this.gift.id;
      if (!gid) return;
      document.querySelectorAll('form[data-dc-add]').forEach((form) => {
        if (form.querySelector('[name="items[1][id]"]')) return;
        const main = form.querySelector('[data-variant-id]');
        if (!main) return;
        main.name = 'items[0][id]';
        const add = (name, value) => {
          const input = document.createElement('input');
          input.type = 'hidden';
          input.name = name;
          input.value = value;
          form.appendChild(input);
        };
        add('items[0][quantity]', '1');
        add('items[1][id]', String(gid));
        add('items[1][quantity]', '1');
      });
    }

    // El CTA final y la barra flotante renderizan su tachado con el compare_at pelado,
    // porque no ven los ajustes del regalo. Sin esto mostrarían un tachado distinto al
    // del hero hasta que la clienta tocara un color, momento en el que saltarían al
    // total inflado. Se alinean con el hero desde el primer pintado.
    syncGiftCompare() {
      if (!this.gift) return;
      const hero = this.querySelector('[data-compare]');
      if (!hero) return;
      const value = hero.textContent;
      document.querySelectorAll('[data-compare]').forEach((el) => {
        if (el !== hero) el.textContent = value;
      });
    }

    // El número del ahorro acaba de cambiar: se le reinicia el pop para que el gesto
    // de elegir color también reafirme que el ahorro sigue ahí en el color nuevo.
    // Se quita la clase y se fuerza un reflow antes de volver a ponerla, si no el
    // navegador ignora el reinicio de la animación.
    // El temporizador la retira al terminar: si la clase se quedara puesta, el pill
    // perdería su latido de fondo a partir del primer toque de color.
    popSaving() {
      if (this.reduceMotion) return;
      const pills = document.querySelectorAll('[data-gift-saving]');
      pills.forEach((el) => {
        el.classList.remove('is-popping');
        void el.offsetWidth;
        el.classList.add('is-popping');
      });
      clearTimeout(this._savingTimer);
      this._savingTimer = setTimeout(() => {
        pills.forEach((el) => el.classList.remove('is-popping'));
      }, 1850);
    }

    // Cascada de entrada: 600ms de espera y 120ms entre piezas. La espera es lo que
    // convierte al regalo en una aparición en vez de en un elemento más del hero.
    revealGiftPieces() {
      const pieces = [...document.querySelectorAll('[data-gift-piece]')];
      if (!pieces.length) return;
      if (this.reduceMotion) { pieces.forEach((p) => p.classList.add('is-in')); return; }
      setTimeout(() => {
        pieces.forEach((p, i) => setTimeout(() => p.classList.add('is-in'), i * 120));
      }, 600);
    }

    // `tilt` es true solo cuando el cambio lo provoca un toque del usuario.
    select(i, tilt) {
      const c = this.colors[i];
      if (!c) return;
      this.active = i;

      // ---- Inmediato: lo barato y lo que afecta a la compra ----

      // Swatch activo (feedback visual del toque)
      this.querySelectorAll('[data-color-index]').forEach((sw) => {
        const on = parseInt(sw.dataset.colorIndex, 10) === i;
        sw.classList.toggle('is-active', on);
        sw.setAttribute('aria-checked', on ? 'true' : 'false');
      });

      // Nombre de color (en toda la landing, no solo el hero)
      document.querySelectorAll('[data-color-name]').forEach((n) => { n.textContent = c.name; });

      // Variante para el carrito + precio (formularios en hero y CTA final).
      // Va síncrono a propósito: es lo que se envía al carrito, nunca puede ir
      // un frame por detrás de lo que el usuario ve seleccionado.
      const v = this.variants.find((x) => (x.color || '').toLowerCase() === (c.name || '').toLowerCase());
      if (v) {
        document.querySelectorAll('[data-variant-id]').forEach((inp) => { inp.value = v.id; });
        if (v.price) document.querySelectorAll('[data-price]').forEach((el) => { el.textContent = v.price; });
        // Con regalo manda compareShown: ya viene resuelto desde Liquid según el
        // ajuste "sumar el regalo al tachado", así que aquí no se decide nada.
        // Se asigna aunque venga vacío, para borrar el tachado de la variante
        // anterior cuando esta no tiene precio antes.
        // Precio, tachado y ahorro cambian juntos o la oferta deja de cuadrar.
        if (this.gift && v.compareShown !== undefined) {
          document.querySelectorAll('[data-compare]').forEach((el) => { el.textContent = v.compareShown; });
          // Solo existe con la burbuja en modo "ahorro calculado"; con texto propio
          // no hay nada que actualizar y el forEach no encuentra a nadie.
          document.querySelectorAll('[data-saving-value]').forEach((el) => { el.textContent = v.savingShown; });
          if (tilt) this.popSaving();
        } else if (v.compareAt) {
          document.querySelectorAll('[data-compare]').forEach((el) => { el.textContent = v.compareAt; });
        }
        // El badge de descuento viene calculado desde Liquid sobre ese mismo tachado.
        // Se oculta en vez de vaciarse: un badge vacío deja una pastilla de color
        // flotando sobre la foto.
        document.querySelectorAll('[data-disc]').forEach((el) => {
          el.textContent = v.discount || '';
          el.hidden = !v.discount;
        });
      }

      // Avisa a la escasez (dc-scarcity) para que el stock + barra cambien por color
      document.dispatchEvent(new CustomEvent('dc:color', { detail: { index: i, name: c.name } }));

      // ---- Un frame después: lo caro ----
      // Cambiar las variables de :root obliga a recalcular el estilo de la página
      // entera y dispara decenas de transiciones a la vez. Aplazarlo un frame deja
      // que el toque se pinte primero (mejor INP) sin que se note el retraso.
      const repaint = () => {
        root.style.setProperty('--dc-accent', c.accent);
        root.style.setProperty('--dc-accent-dark', c.dark);
        root.style.setProperty('--dc-accent-soft', c.soft);
        root.style.setProperty('--dc-accent-deep', c.deep);
        root.style.setProperty('--dc-glow', c.accent + '55');

        // Imagen principal (+ tilt) — en todas las secciones
        if (c.image) {
          document.querySelectorAll('[data-main-image]').forEach((img) => {
            // El srcset solo se toca donde ya existia. La miniatura de la barra
            // fija mide 46px y no lleva `sizes`: darle el srcset del hero la haria
            // bajar la version mas grande de todas.
            const nextSet = c.srcset || '';
            if (img.hasAttribute('srcset') && img.getAttribute('srcset') !== nextSet) img.srcset = nextSet;
            // Si la foto ya es esta, no se reasigna: reasignar src vacia la imagen
            // y provocaria un parpadeo del cargador en la carga inicial, cuando el
            // color activo es el que ya venia pintado desde el servidor.
            if (img.getAttribute('src') !== c.image) {
              img.src = c.image;
              // Mientras llega la foto del color nuevo, el hueco muestra el
              // cargador de marca en vez de quedarse en blanco.
              if (window.DCCore && window.DCCore.restartLoader) window.DCCore.restartLoader(img);
            }
            if (tilt) {
              const deg = i % 2 === 0 ? -2.2 : 2.2;
              img.style.transform = `rotate(${deg}deg) scale(1.03)`;
              setTimeout(() => { img.style.transform = 'none'; }, 500);
            }
          });
        }

        // El botón solo se apropia del color cuando el usuario lo eligió (tilt).
        // En la carga el color activo es un default que nadie tocó: no hay nada que reforzar.
        if (tilt) this.reinforceCTA(c);
      };

      // En la carga inicial se aplica ya, para que no haya un parpadeo de color.
      if (tilt) requestAnimationFrame(repaint); else repaint();
    }

    // El CTA pasa a nombrar el color elegido y se resalta, para que la energía del
    // toque termine en la acción de comprar y no en la imagen.
    reinforceCTA(c) {
      const btn = this.querySelector('[data-cta-color-tpl]');
      if (!btn || !c.name) return; // sin plantilla configurada (o galería sin nombre): nada que hacer

      // 1) Texto: la capa del color reemplaza a la de siempre y el rotador se detiene.
      const out = btn.querySelector('[data-cta-color]');
      const base = btn.querySelector('[data-cta-base]');
      if (out) {
        out.textContent = btn.getAttribute('data-cta-color-tpl').replace('{color}', c.name);
        out.hidden = false;
      }
      if (base) {
        base.hidden = true;
        const rot = base.querySelector('dc-rotator');
        if (rot) rot.setAttribute('data-paused', '');
      }
      btn.classList.add('is-color-picked');

      // 2) Pulso. Se quita y se vuelve a poner (con reflow en medio) para que tocar
      //    varios colores seguidos reinicie la animación en vez de ignorarla.
      btn.classList.remove('is-pulsing');
      void btn.offsetWidth;
      btn.classList.add('is-pulsing');
      clearTimeout(this._pulseTimer);
      this._pulseTimer = setTimeout(() => btn.classList.remove('is-pulsing'), 950);

      // 3) Microscroll: solo si el botón no se ve entero. Moverle la página a quien
      //    ya lo tiene delante se siente como que la web se le fue de las manos.
      if (this.reduceMotion) return;
      const r = btn.getBoundingClientRect();
      const vh = window.innerHeight || document.documentElement.clientHeight;
      const headroom = 64; // header sticky
      if (r.top >= headroom && r.bottom <= vh - 12) return;
      window.scrollTo({
        top: Math.max(0, window.scrollY + r.top - Math.max(headroom, (vh - r.height) / 2)),
        behavior: 'smooth',
      });
    }

    // Toque en la mini-card del regalo: lleva al botón de compra del propio hero, no
    // al del final de la landing, y lo pulsa. Así la curiosidad por el regalo termina
    // en la acción de comprar en vez de saltarse toda la página.
    scrollToHeroCTA() {
      const btn = this.querySelector('.dc-hero__cta') || this.querySelector('[data-dc-add] [type="submit"]');
      if (!btn) { this.scrollToBuy(); return; }

      const pulse = () => {
        btn.classList.remove('is-pulsing');
        void btn.offsetWidth;
        btn.classList.add('is-pulsing');
        clearTimeout(this._pulseTimer);
        this._pulseTimer = setTimeout(() => btn.classList.remove('is-pulsing'), 950);
      };

      const r = btn.getBoundingClientRect();
      const vh = window.innerHeight || document.documentElement.clientHeight;
      const headroom = 64; // header sticky
      // Si ya se ve entero, no le movemos la página a quien ya lo tiene delante:
      // solo lo pulsamos para señalar dónde está.
      if (r.top >= headroom && r.bottom <= vh - 12) { pulse(); return; }
      if (this.reduceMotion) {
        window.scrollTo({ top: Math.max(0, window.scrollY + r.top - headroom) });
        pulse();
        return;
      }
      window.scrollTo({
        top: Math.max(0, window.scrollY + r.top - Math.max(headroom, (vh - r.height) / 2)),
        behavior: 'smooth',
      });
      setTimeout(pulse, 520);
    }

    scrollToBuy() {
      const el = document.getElementById('comprar');
      if (el) window.scrollTo({ top: el.offsetTop - 60, behavior: 'smooth' });
    }

    // Agrega la variante seleccionada al carrito y lleva directo al checkout
    addAndCheckout(form) {
      const btn = form.querySelector('[type="submit"]');
      if (btn) { if (btn.disabled) return; btn.setAttribute('aria-busy', 'true'); }
      fetch(form.getAttribute('action'), {
        method: 'POST',
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: new FormData(form),
      })
        .then((r) => {
          if (r.ok) { window.location.assign('/checkout'); return; }
          if (btn) btn.removeAttribute('aria-busy');
          form.submit(); // si el add falla, deja el flujo nativo (muestra el error / return_to)
        })
        .catch(() => { form.submit(); }); // sin conexión: fallback nativo con return_to=/checkout
    }
  }

  customElements.define('dc-landing', DCLanding);
  // Alias de compatibilidad: plantillas ya publicadas que todavía emiten <dc-almohada>.
  customElements.define('dc-almohada', class extends DCLanding {});
})();
